import { CanvasMark } from '../plane/CanvasMark';
import { StatusGlyph } from '../board/StatusGlyph';
import type { Status } from '../plan/arc';

/** A status glyph and a name, "waiting on you" when it is — the only status
 *  an arc says in words here — and a storyline's open arcs. With
 *  [onPickStatus] the glyph is a control; it carries its rect so the picker
 *  can open against it. */
export function ArcLine({
  title,
  status,
  inside = 0,
  onCanvas = false,
  onPickStatus,
}: {
  title: string;
  status: Status;
  inside?: number;
  onCanvas?: boolean;
  onPickStatus?: (anchor: DOMRect) => void;
}) {
  return (
    <span className="nook-arc">
      {onPickStatus ? (
        <button
          type="button"
          className="status-button nook-arc-glyph nook-arc-status"
          title={`${status} — change`}
          aria-label={`Status: ${status}. Change`}
          onClick={(e) => {
            e.stopPropagation();
            onPickStatus(e.currentTarget.getBoundingClientRect());
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <StatusGlyph status={status} size={11} />
        </button>
      ) : (
        <span className="nook-arc-glyph">
          <StatusGlyph status={status} size={11} />
        </span>
      )}
      <span className="nook-arc-text">
        <span className="nook-arc-name">{title}</span>
        {status === 'needs-input' && <span className="nook-waiting">waiting on you</span>}
        {inside > 0 && <span className="nook-inside">{inside} inside</span>}
      </span>
      {onCanvas && <CanvasMark />}
    </span>
  );
}

export function PinGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 17v4M8 3h8l-1 6 3 3v2H6v-2l3-3z" />
    </svg>
  );
}

export function DockGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <path d="M14 4.5v15" />
    </svg>
  );
}

export function FloatGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <rect x="9" y="9" width="8" height="7" rx="1.5" />
    </svg>
  );
}

export function CloseGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function PenGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    </svg>
  );
}

export function BackGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

export function PlayGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function ExpandGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6M20 4l-6 6M10 20H4v-6M4 20l6-6" />
    </svg>
  );
}

export function OpenGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6M10 14L20 4M20 14v6H4V4h6" />
    </svg>
  );
}

export function NoteGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M5 7h14M5 12h14M5 17h9" />
    </svg>
  );
}

export function DeskGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11h18M6 11v8M18 11v8" />
      <rect x="8" y="5" width="8" height="6" rx="1.2" />
    </svg>
  );
}

/** An arc waits on you: a ringed question mark in the waiting ink. */
export function WaitingMark({ label = 'An arc waits on you' }: { label?: string }) {
  return (
    <span className="waiting-mark" role="img" aria-label={label}>
      ?
    </span>
  );
}
